/**
 * OC FAB Widget Component
 * Floating Action Button widget with sliding chat panel
 * Handles authentication, panel toggle, and session management
 */

import { Alert } from '@hl/ui-components';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';

import { EMPTY_GUID } from '../../constants';
import { OcAuthResponse, OcChatConfig } from '../../types/oc';
import { OcFabWidgetOptions } from '../../types/oc';
import { OcSkeletonDefs } from '../Loading/SkeletonSVG';
import { ErrorWrapper } from '../TranscriptBox/TranscriptMessages.styled';
import {
  AuthState,
  OcChatPanel,
  OcChatPanelLoadingShell,
} from './components/ChatPanelContent/OcChatPanel';
import { OcFabWidgetTrigger } from './components/FabButton/OcFabWidgetTrigger';
import { NotificationProvider } from './components/Notification/NotificationProvider';
import {
  ConfigValidationError,
  validateOcChatConfig,
} from './config/configValidator';
import { getOcTheme, IOcAppTheme } from './config/ocTheme';
import {
  OcFabWidgetOptionsProvider,
} from './contexts/OcFabWidgetOptionsContext';
import { useEffectivePrivacyConfig } from './hooks/useEffectivePrivacyConfig';
import { canResolveOcTenant, hasCookieAuth, isOcSiteTransport } from './services/ocApiClient';
import { checkOcAuth } from './services/ocEndpoints';
import { SessionManagerProvider } from './sessionManagement/SessionManagerContext';
import { OcGlobalStyleWrapper } from './styles/OcGlobalStyle';
import { ensureInterStylesheet } from './utils/ensureInterStylesheet';

export interface OcFabWidgetProps {
  config: OcChatConfig;
  options?: OcFabWidgetOptions;
}

/**
 * OcFabWidget Component
 * Main FAB widget with authentication and session management
 */
export const OcFabWidget: FC<OcFabWidgetProps> = ({ config, options }) => {
  // Ensure the Inter font is available when the widget is mounted as a React component.
  // (The external embed API also calls this, but React usage may not.)
  useEffect(() => {
    ensureInterStylesheet();
  }, []);

  const [authState, setAuthState] = useState<AuthState>('checking');
  const [userId, setUserId] = useState<string>('');
  const [isPublicAnonymousUser, setIsPublicAnonymousUser] = useState(false);
  // Set once the auth check has ruled out a member and the only remaining question is whether the
  // tenant allows public access, which privacy answers.
  const [isAwaitingPublicAccess, setIsAwaitingPublicAccess] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(options?.autoOpen ?? false);
  const hasChatBeenOpen = useRef(options?.autoOpen ?? false);
  const originToolId =
    config?.originToolId ||
    config?.aiAssistantKey ||
    '00000000-0000-0000-0000-000000000000';

  // Determine if we're in preview mode
  const isPreviewMode = config.mode === 'preview';
  const previewState = options?.previewState;
  const usePreviewButton = isPreviewMode && !!previewState?.usePreviewButton;

  const requireLogin = options?.requireLogin !== false;

  // Hosts rebuild their config object on every render, so hostAuth must be re-memoized from its
  // primitive fields. Depending on the incoming object reference would give stableConfig a new
  // identity on every host render, re-running the auth effect and the GetPrivacySettings fetch
  // and churning auth state on something as routine as a window resize.
  const hostAuthIsAuthenticated = config.hostAuth?.isAuthenticated;
  const hostAuthUserId = config.hostAuth?.userId;
  const hostAuth = useMemo(
    () =>
      hostAuthIsAuthenticated === undefined
        ? undefined
        : { isAuthenticated: hostAuthIsAuthenticated, userId: hostAuthUserId ?? '' },
    [hostAuthIsAuthenticated, hostAuthUserId],
  );

  // Ref keeps the latest options available inside effects without
  // making the effects re-run when the embedder passes a new object.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Stabilize config so downstream hooks/effects only re-run when fields
  // that affect API identity actually change.
  // isPrivate/isAnonymous are intentionally excluded: they are populated
  // downstream by useEffectivePrivacyConfig after Crest responds.
  // allowPublicAccess/isPublicAnonymousUser are intentionally excluded too: they are
  // *outputs* of the auth check below, not inputs. Including them here would make the
  // auth effect and useEffectivePrivacyConfig depend on their own result, re-running
  // (and re-fetching WhoAmI/GetPrivacySettings) a second time right after they resolve.
  // Callers that need them read the isPublicAnonymousUser state / OcFabWidgetOptions
  // context directly instead of via config.
  // TODO: Manually enumerating fields is fragile -- new OcChatConfig fields must be added here
  // (or intentionally excluded). Consider a utility or documenting the exclusion list instead.
  const stableConfig = useMemo<OcChatConfig>(
    () => ({
      apiBaseUrl: config.apiBaseUrl,
      apiTransport: config.apiTransport,
      recipeCode: config.recipeCode,
      tenantId: config.tenantId,
      aiAssistantKey: config.aiAssistantKey,
      loginUrl: config.loginUrl,
      originToolId: config.originToolId,
      mode: config.mode,
      iamKey: config.iamKey,
      HL_Test: config.HL_Test,
      requireLogin,
      hostAuth,
    }),
    [
      config.apiBaseUrl,
      config.apiTransport,
      config.recipeCode,
      config.tenantId,
      config.aiAssistantKey,
      config.loginUrl,
      config.originToolId,
      config.mode,
      config.iamKey,
      config.HL_Test,
      requireLogin,
      hostAuth,
    ],
  );

  // Privacy comes from GetPrivacySettings (Crest or oc-api depending on apiTransport).
  // Skip when the active transport cannot resolve a tenant / antiforgery context.
  const isAuthAvailable = canResolveOcTenant(stableConfig);
  const privacyResult = useEffectivePrivacyConfig(stableConfig, isAuthAvailable);

  // Treat privacy load failure as a fatal error — but only once we know the user is
  // authenticated.  While auth is still checking, a 401 from the privacy endpoint
  // may simply mean the user isn't logged in; reporting it as an error would hide
  // the login screen. Anonymous public visitors never reach 'authenticated' on a failed
  // privacy load, because the effect below refuses them before they get there.
  useEffect(() => {
    if (isPreviewMode || privacyResult.status !== 'error') return;
    if (authState !== 'authenticated') return;
    // A visitor waiting on the public-access decision is that effect's to refuse, so leaving it
    // here as well would report the same failure twice.
    if (isAwaitingPublicAccess) return;
    setError(privacyResult.error);
    setAuthState('error');
    optionsRef.current?.onError?.(privacyResult.error);
  }, [privacyResult, isPreviewMode, authState, isAwaitingPublicAccess]);

  // Check authentication on mount (skip in preview mode)
  useEffect(() => {
    const opts = optionsRef.current;
    setIsAwaitingPublicAccess(false);

    // In preview mode, set auth state based on previewState
    if (isPreviewMode) {
      if (!previewState) {
        const error = new Error('Preview mode requires previewState to be provided');
        setError(error);
        setAuthState('error');
        opts?.onError?.(error);
        return;
      }

      // Set auth state based on previewState.authState
      if (previewState.authState === 'authenticated') {
        setUserId('preview-user-id');
        setAuthState('authenticated');
        if (opts?.onReady) {
          setTimeout(opts.onReady, 0);
        }
      } else {
        setAuthState('unauthenticated');
        opts?.onAuthRequired?.();
      }
      return;
    }

    // Production mode - check authentication normally
    const checkAuth = async () => {
      try {
        validateOcChatConfig(stableConfig);

        // crest: needs iamKey or HLTenantKey cookie.
        // ocSite: needs antiforgery token (tenant comes from OC host context).
        // Anonymous on-site visitors do not receive Crest auth cookies.
        if (!canResolveOcTenant(stableConfig)) {
          setAuthState('unauthenticated');
          optionsRef.current?.onAuthRequired?.();
          return;
        }

        // On the OC site the host page already knows the answer: it renders it from the same
        // CurrentUser.ContactKey that oc-api uses to classify the chat caller. Trust it and skip
        // WhoAmI entirely, because the HLAuthToken behind WhoAmI is a stateless 24-hour token
        // that outlives the OC session and reports signed-out visitors as members.
        const hostAuthState = isOcSiteTransport(stableConfig) ? stableConfig.hostAuth : undefined;

        // null means "no member credential to evaluate" -- neither a host claim nor cookies/iamKey.
        let memberAuth: OcAuthResponse | null = null;

        if (hostAuthState) {
          memberAuth = {
            isAuthenticated:
              hostAuthState.isAuthenticated &&
              !!hostAuthState.userId &&
              hostAuthState.userId !== EMPTY_GUID,
            userId: hostAuthState.userId,
          };
        } else if (hasCookieAuth() || !!(stableConfig.iamKey && stableConfig.iamKey.trim() !== '')) {
          // WhoAmI is Crest-only; external embeds authenticate with an iamKey or Crest cookies.
          memberAuth = await checkOcAuth(stableConfig);
        }

        if (memberAuth?.isAuthenticated) {
          if (!memberAuth.userId) {
            throw new Error('Auth response missing userId');
          }
          setUserId(memberAuth.userId);
          setIsPublicAnonymousUser(false);
          setAuthState('authenticated');

          if (optionsRef.current?.onReady) {
            setTimeout(optionsRef.current.onReady, 0);
          }
          return;
        }

        // Stale or missing OC session should not block public access when login is optional.
        if (requireLogin) {
          setAuthState('unauthenticated');
          optionsRef.current?.onAuthRequired?.();
          return;
        }

        // requireLogin is always false here -- the branch above returns early when it's true.
        // The tenant's public-access policy is part of the GetPrivacySettings response that
        // useEffectivePrivacyConfig is already fetching, so hand off to the effect below rather
        // than asking a second time. Two fetches of the same endpoint can resolve differently:
        // the probe granting access used to report the widget ready while the hook's copy failed
        // a moment later and flipped that same working widget into an error state.
        setIsAwaitingPublicAccess(true);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setAuthState('error');
        optionsRef.current?.onError?.(error);
      }
    };

    checkAuth();
  }, [stableConfig, isPreviewMode, previewState, requireLogin]);

  // Finish the anonymous public visitor's auth check once privacy has loaded. This is a separate
  // effect because the answer arrives from a fetch the auth effect does not own; adding
  // privacyResult to that effect's dependencies would re-run the member checks -- including
  // WhoAmI -- every time privacy resolved.
  useEffect(() => {
    if (!isAwaitingPublicAccess || privacyResult.status === 'loading') return;

    setIsAwaitingPublicAccess(false);

    // Privacy is what says whether this visitor is allowed in at all, so a failed load is fatal
    // here rather than something to report after going ready.
    if (privacyResult.status === 'error') {
      setError(privacyResult.error);
      setAuthState('error');
      optionsRef.current?.onError?.(privacyResult.error);
      return;
    }

    // Also covers allowPublicAccess being absent from the response: an unstated policy is not
    // permission, and a login prompt is the right answer rather than an error.
    if (!privacyResult.config.allowPublicAccess) {
      setAuthState('unauthenticated');
      optionsRef.current?.onAuthRequired?.();
      return;
    }

    setUserId('');
    setIsPublicAnonymousUser(true);
    setAuthState('authenticated');

    if (optionsRef.current?.onReady) {
      setTimeout(optionsRef.current.onReady, 0);
    }
  }, [isAwaitingPublicAccess, privacyResult]);

  // Build theme based on themeMode and useSVGBackground
  const themeMode = options?.themeMode || 'light';
  const useSVGBackground = options?.useSVGBackground !== false; // default true
  const baseTheme = getOcTheme(themeMode, useSVGBackground);

  const theme: IOcAppTheme = options?.theme
    ? {
      ...baseTheme,
      color: {
        ...baseTheme.color,
        ...options.theme.color,
      },
      font: {
        size: {
          ...baseTheme.font.size,
          ...options.theme.font?.size,
        },
        weight: {
          ...baseTheme.font.weight,
          ...options.theme.font?.weight,
        },
      },
    }
    : baseTheme;

  // Handle FAB click
  const handleFabClick = () => {
    const newState = !isChatOpen;
    setIsChatOpen(newState);
    hasChatBeenOpen.current = true;

    if (options?.onFabClick) {
      options.onFabClick();
    }

    if (options?.onPanelToggle) {
      options.onPanelToggle(newState);
    }
  };

  // Handle panel close
  const handlePanelClose = () => {
    setIsChatOpen(false);

    if (options?.onPanelToggle) {
      options.onPanelToggle(false);
    }
  };

  // Error state (configuration errors only - don't show FAB)
  if (
    authState === 'error' &&
    error &&
    error instanceof ConfigValidationError
  ) {
    const errorMessage = `Configuration Error: ${error.message}`;

    return (
      <>
        <OcGlobalStyleWrapper />
        <ThemeProvider theme={theme}>
          <OcSkeletonDefs />
          <ErrorWrapper>
            {/* @ts-expect-error HL components is on an older react version where children is implicitly included */}
            <Alert danger={true}>{errorMessage}</Alert>
          </ErrorWrapper>
        </ThemeProvider>
      </>
    );
  }

  return (
    <>
      <OcGlobalStyleWrapper />
      <ThemeProvider theme={theme}>
        <OcSkeletonDefs />
          <OcFabWidgetOptionsProvider options={options} isPublicAnonymousUser={isPublicAnonymousUser}>
            <NotificationProvider>
              {/* Trigger button - always visible (even when checking/unauthenticated) */}
              <OcFabWidgetTrigger
                usePreviewButton={usePreviewButton}
                onClick={handleFabClick}
              />

              {/* Chat Panel - lazy loaded after first open.
                  Unauthenticated users bypass the privacy gate when login is not required.
                  Auth must be resolved before mounting SessionManagerProvider so that
                  userId is available for cross-user session validation. */}
              {hasChatBeenOpen.current &&
                (authState !== 'checking' &&
                (privacyResult.status === 'success' || authState === 'unauthenticated') ? (
                  <SessionManagerProvider
                    config={privacyResult.config}
                    originToolId={originToolId}
                    isAuthenticated={authState === 'authenticated'}
                    isPublicAnonymousUser={isPublicAnonymousUser}
                    userId={userId}
                    previewState={previewState}
                  >
                    <OcChatPanel
                      config={privacyResult.config}
                      userId={userId}
                      isPublicAnonymousUser={isPublicAnonymousUser}
                      authState={authState}
                      error={error}
                      theme={theme}
                      isOpen={isChatOpen}
                      onClose={handlePanelClose}
                    />
                  </SessionManagerProvider>
                ) : (
                  <OcChatPanelLoadingShell
                    theme={theme}
                    isOpen={isChatOpen}
                    onClose={handlePanelClose}
                    error={privacyResult.status === 'error' ? privacyResult.error : null}
                  />
                ))}
            </NotificationProvider>
          </OcFabWidgetOptionsProvider>
      </ThemeProvider>
    </>
  );
};

export default OcFabWidget;
